import { Loader, Table, Group, Button } from '@mantine/core';
import { useQuery, UseQueryResult, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import { getLanguageName } from '../../languages/languageUtils';
import {
  fetchUsers,
  IUserResponse,
  fetchPermissions,
  PermissionOptionResponse,
  IUser,
  deleteUser,
  ImportCSVReport
} from '../../../libraries/user/index';
import Header from '../../components/util/header';
import { LoginState } from '../../client-redux/login/reducer';
import PaginationComponent from '../../components/pagination';
import { useState, useEffect, useRef } from 'react';
import FilterBar, { FilterBarFieldsData } from '../../components/filterbar';
import { useFitTableRows } from '../../components/util/fit-table-rows-hook';
import ConfirmationModal from '../../components/confirmation-modal';
import UserModal from './user-modal';
import { UserFormMode } from './user-form';
import { fileToBase64 } from '../../utils/script';
import CustomModal from '../../components/custom-modal';
import * as Icon from '@tabler/icons-react';
import './styles.scss';

function Users() {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const login = useSelector<AppState, LoginState>(state => state.login);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const currentEntid = useSelector<AppState, number>(state => state.filters.filterBar.entity ?? 0);
  const controller = new AbortController();
  const permissionOptionData: UseQueryResult<PermissionOptionResponse> = useQuery([], () => fetchPermissions());
  const [loading, setLoading] = useState<boolean>(false);
  const {
    limit = 10,
    setLimit,
    tableRef
  } = useFitTableRows({
    bufferRows: 1,
    minHeightForDynamic: 400
  });
  const [offset, setOffset] = useState<number>(0);
  const [totalRowsResponse, setTotalRowsResponse] = useState<number | undefined>();
  const [selectedUser, setSelectedUser] = useState<IUser>();
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [message, setMessage] = useState<IUserResponse>();
  const [noResults, setNoResults] = useState<boolean>(false);
  const [statusOperCode, setStatusOperCode] = useState<number | undefined>();
  const queryClient = useQueryClient();

  const [filterBarData, setFilterBarData] = useState<FilterBarFieldsData>();
  const [showNewUserModal, setShowNewUserModal] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<number | null>(null);

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
const [showErrorDialog, setShowErrorDialog] = useState(false);
const [modalMessage, setModalMessage] = useState('');

  useEffect(() => {
    if (filterBarData && limit) {
      loadUsersList();
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBarData, limit, offset]);

  const handleFilterBarOnDataChange = (data: FilterBarFieldsData) => {
    setFilterBarData(data);
    changePage(1);
  };

  const loadUsersList = () => {
    if (!login.token) {
      return;
    }
    if (loading) {
      setNoResults(false);
      controller.abort();
    }
    setLoading(true);
    const shouldFetchTotalRows = offset === 0;

    fetchUsers(
      controller,
      filterBarData?.entity || login.user.entid || 0,
      limit,
      offset,
      shouldFetchTotalRows,
      filterBarData?.name || '',
      filterBarData?.uid || 0
    )
      .then(response => {
        if (response.statusOper.code === 0) {
          setMessage(response);
          if (shouldFetchTotalRows && typeof response.totalRows === 'number') {
            setTotalRowsResponse(response.totalRows);
          }
          setNoResults(false);
          setStatusOperCode(response.statusOper.code);
          setLoading(false);
        } else {
          setNoResults(true);
          setLoading(false);
          setStatusOperCode(response.statusOper.code);
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') {
        } else {
        }
      })
      .finally(() => setLoading(false));
  };

  const handleToggle =
    (index: number): (() => void) =>
    () => {
      setSelectedUser(message?.users[index]);
      setOpenDetails(!openDetails);
    };

  const isActive = (uid: number | null): boolean => {
    if (!selectedUser) return false;

    return uid === selectedUser.uid;
  };

  const closeDetailsModal = () => {
    setOpenDetails(false);
  };

  const userUpdated = () => {
    closeDetailsModal();
    loadUsersList();
  };

  const changePage = (page: number) => {
    const newOffset = (page - 1) * limit;
    setCurrentPage(page);
    setOffset(newOffset);
  };

  const resultsSelect = (newValue: number) => {
    const newLimit = Number(newValue);
    setLimit(newLimit);
    setCurrentPage(1);
    setOffset(0);
  };

  const handleAddUserToList = () => {
    setShowNewUserModal(true);
  };

  const deleteActiveUser = async (entid: number, uid: number) => {
    try {
      const res = await deleteUser(entid, uid);
      if (res?.statusOper?.code === 0) {
        queryClient.invalidateQueries('users');
        loadUsersList();
        setOpenDetails(false);
      } else {
        console.error('Failed to delete user:', res?.statusOper?.message);
      }
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };


const handleCSVFileImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
  const file = event.target.files?.[0];
  if (!file) return;

  try {
    const base64 = await fileToBase64(file);

    const res = await ImportCSVReport(
      {
        entid: currentEntid || login.user.entid,
        file: base64
      },
      controller
    );

    if (res.statusOper.code === 0) {
      setModalMessage(lang.USER_IMPORT_SUCCESS);
      setShowSuccessDialog(true);
      loadUsersList();
    } else {
      setModalMessage( `${lang.USER_IMPORT_ERROR}: ${res.statusOper.code} / ${res.statusOper.message}`);
      setShowErrorDialog(true);
    }
  } catch (err) {
    console.error(err);
    setModalMessage(lang.IMPORT_ERROR);
    setShowErrorDialog(true);
  } finally {
    event.target.value = '';
    setTimeout(() => setShowSuccessDialog(false), 2000);
    setTimeout(() => setShowErrorDialog(false), 3000);
  }
};

  return (
    <div className="users">
      <ConfirmationModal
        opened={deleteCandidate !== null}
        onClose={() => setDeleteCandidate(null)}
        onConfirm={async () => {
          if (deleteCandidate !== null) {
            await deleteActiveUser(currentEntid, deleteCandidate);
          }
        }}
        text={lang.USER_DELETE_CONFIRM}
        successText={lang.ATYPE_USER_DELETED}
        errorText={lang.EDIT_USER_ALERT_DELETE_ERROR}
      />

      {selectedUser && lang && permissionOptionData.data && (
        <UserModal
          show={openDetails}
          mode={UserFormMode.UPDATE}
          user={selectedUser}
          lang={lang}
          permissionOptions={permissionOptionData.data.list}
          onClose={closeDetailsModal}
          onUserAdded={userUpdated}
          OnUserDeleted={(uid: number) => setDeleteCandidate(uid)}
        />
      )}

      {permissionOptionData.data && (
        <UserModal
          show={showNewUserModal}
          mode={UserFormMode.CREATE}
          user={{
            entid: login.user.entid,
            uid: null,
            lang: login.user.lang,
            region: login.user.region
          }}
          lang={lang}
          permissionOptions={permissionOptionData.data.list}
          onClose={() => setShowNewUserModal(false)}
          onUserAdded={loadUsersList}
        />
      )}

      <Header pageTitle={lang.GLOBAL_USERS} />

      <Group>
        <FilterBar
          onSearch={loadUsersList}
          onDataChange={handleFilterBarOnDataChange}
          fieldsUsage={{
            entity: true,
            name: true,
            uid: true
          }}
          addButton={handleAddUserToList}
        />
        <Button radius="md" onClick={() => fileInputRef.current?.click()}>
  <Icon.IconFileTypeCsv stroke={2} />
  {lang.USER_IMPORT_CSV_FILE}
</Button>

<input
  type="file"
  accept=".csv"
  ref={fileInputRef}
  style={{ display: 'none' }}
  onChange={handleCSVFileImport}
/>
      </Group>
            
      <Table.ScrollContainer minWidth={500} ref={tableRef}>
        <Table striped highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>ID</Table.Th>
              <Table.Th>{lang.GLOBAL_NAME}</Table.Th>
              <Table.Th>{lang.USER_EMAIL}</Table.Th>
              <Table.Th>{lang.USER_CARD}</Table.Th>
              <Table.Th>{lang.USER_REGION}</Table.Th>
              <Table.Th>{lang.USER_LANG}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {loading && !message && (
              <Table.Tr>
                <Table.Td colSpan={16}>
                  <Loader color="#016273" />
                </Table.Td>
              </Table.Tr>
            )}
            {noResults && !loading ? (
              <Table.Tr>
                <Table.Td colSpan={16} style={{ textAlign: 'center' }}>
                  {lang.GLOBAL_NO_RESULTS}
                </Table.Td>
              </Table.Tr>
            ) : (
              message?.users?.map((user: IUser, index: number) => (
                <Table.Tr
                  key={user.uid}
                  className={`table-row ${isActive(user.uid) ? 'selectedUser' : ''}`}
                  onClick={handleToggle(index)}
                >
                  <Table.Td>{user.uid}</Table.Td>
                  <Table.Td>{user.name}</Table.Td>
                  <Table.Td>{user.email}</Table.Td>
                  <Table.Td>{user.card}</Table.Td>
                  <Table.Td>{user.region}</Table.Td>
                  <Table.Td>{getLanguageName(user.lang)}</Table.Td>
                  <Table.Td
                    onClick={e => {
                      e.stopPropagation();
                      if (!openDetails) setDeleteCandidate(user.uid);
                    }}
                  >
                    <Icon.IconTrash stroke={2} />
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>

      <Group>
        <PaginationComponent
          total={totalRowsResponse}
          statusOperCode={statusOperCode}
          itemsPerPage={limit}
          dynamicLimit={limit}
          currentPage={currentPage}
          onPageChange={changePage}
          onResultsChange={resultsSelect}
          offset={offset}
        />
      </Group>

      <CustomModal
  opened={showSuccessDialog}
  onClose={() => setShowSuccessDialog(false)}
  message={modalMessage}
  backgroundColor="#3BDF70"
  textColor="black"
/>

<CustomModal
  opened={showErrorDialog}
  onClose={() => setShowErrorDialog(false)}
  message={modalMessage}
  backgroundColor="#E3353F"
  textColor="white"
/>
    </div>
  );
}

export default Users;
